<script setup lang="ts">
//#region imports
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ref, watch, computed, toRaw } from 'vue';
import { useSessionAction } from '@/composables/useSessionAction';
import { useApi } from '@/api';
import { useDetectPermissions } from '@/composables/useDetectPermissions';
import { usePopupManager } from '@/components/PopupManager.vue';
import _ from '@/setup/lodash';
import { useEventBus } from '@/setup/eventBus';
import type { GetTeamsByIdPermissionsResponse, GetAdminsResponse } from '@/types/openapi-ts';
//#endregion

//#region types
type TeamItem = {
  id: string,
  name: string,
  type: 'owners' | 'employees';
  members_count: number;
};
type TeamPermissions = GetTeamsByIdPermissionsResponse['payload'];
type AdminItem = GetAdminsResponse['payload'][number];
//#endregion

//#region composables
const route = useRoute();
const router = useRouter();
const api = useApi();
const { t } = useI18n();
const eventBus = useEventBus();
const popupManager = usePopupManager();
const {checkRequest, checkRoute} = useDetectPermissions();
const { onSessionUnlock } = useSessionAction();
//#endregion

//#region state
const isDataLoading = ref(false);
const searchQuery = ref('');

const dataTeams = ref<TeamItem[]>([]);
const allUsers = ref<AdminItem[]>([]);

const dataPermissions = ref<Partial<TeamPermissions>>({
  affiliates_types_edit: [],
  affiliates_types_view: [],
  players_available_properties: [],
  reports_affiliates_types: [],
});
//#endregion

//#region computed
const teamsList = computed<TeamItem[]>(() => {
  const rawTeams = dataTeams.value ? toRaw(dataTeams.value) : [];
  return [
    {
      id: 'all',
      name: t('teams.teams_fields.default_team'),
      type: 'owners',
      members_count: allUsers.value.length || 0,
    },
    ...structuredClone(rawTeams),
  ];
});

const activeTeam = computed<TeamItem>(() => {
  if(!route.params.id) {
    return  teamsList.value[0]!;
  }
  const routeTeamId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
  return teamsList.value.find((item) => routeTeamId === item.id)!;
});
//#endregion

//#region methods
async function initData() {
  if (checkRequest('teams_view')) {
    await getTeams();
    getUsers();
  }
  checkRoute('teams_view');
}

async function getTeams() {
  isDataLoading.value = true;

  try {
    const response = await api.getTeamsList({ search: searchQuery.value });
    dataTeams.value = _.cloneDeep(response.data.payload);
    // if (activeTeam.value && activeTeam.value.type !== 'owners') {
    //   await getPermissionsPromise(activeTeam.value.id);
    // }
  } finally {
    isDataLoading.value = false;
  }
}

function getUsers() {
  api.getAdmins({}).then((response) => {
    allUsers.value = response.data.payload;
  });
}

async function getPermissionsPromise(teamId: string) {
  const response = await api.getTeamPermissions(teamId);
  dataPermissions.value = _.cloneDeep(response.data.payload);
}

async function getPermissions() {
  if (activeTeam.value && activeTeam.value.type !== 'owners') {
    isDataLoading.value = true;
    try {
      await getPermissionsPromise(activeTeam.value.id);
    } finally {
      isDataLoading.value = false;
    }
  }
}

async function refresh() {
  await getTeams();
  getUsers();
}

function addTeamHandler() {
  popupManager.teamPopup.open({
    listeners: {
      success: async (payload) => {
        await getTeams();
        await router.push(`/teams/${payload.id}/details/users`);
        popupManager.attachUserToTeam.open({
          data: {
            context: {
              mode: 'select-user',
              team: payload,
            },
          },
          listeners: {
            refresh: async () => {
              await getTeams();
              eventBus.$emit('addUserNewTeam');
            },
          },
        });
      },
    },
  });
}

function deleteTeamHandler(teamItem: TeamItem) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('team_popup.delete_team_title', { name: teamItem.name }),
      message: t('team_popup.delete_team_msg', { name: teamItem.name }),
      bodyIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmIcon: 'trash-alt',
      confirmTitle: t('team_popup.delete_team_action'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;

        try {
          await api.deleteTeam(teamItem.id);
          router.push('/teams/all/details/users');
          await getTeams();
        } catch (error) {
          window.console.error(error);
        }

        context.handleClose();
      },
    },
  });
}

function editTeamHandler(teamItem: TeamItem) {
  popupManager.teamPopup.open({
    data: {
      teamId: teamItem.id,
      teamName: teamItem.name,
    },
    listeners: {
      success: async (payload) => {
        if(payload.id !== route.params.id) {
          router.push(`/teams/${payload.id}/details/users`);
        }
        await getTeams();
      },
    },
  });
}

function updatePermissions(data: TeamPermissions) {
  dataPermissions.value = data;
}
//#endregion

//#region watch
watch(() => activeTeam.value?.id, getPermissions);
//#endregion

//#region hooks
onSessionUnlock.value = initData;
initData();
//#endregion
</script>

<template>
  <div
    v-loading="isDataLoading"
    class="teams-list"
  >
    <div class="t:flex t:w-full t:gap-32">
      <div class="t:w-1/3 t:space-y-16">
        <UiTitle
          :font-size="24"
          class="t:font-medium"
        >
          {{ $t(`teams.teams_fields.title`) }}
        </UiTitle>
        <div class="t:space-y-8">
          <div class="t:flex t:items-center t:gap-8">
            <UiInput
              v-model.trim="searchQuery"
              is-search
              autosize
              @search="getTeams"
            />
            <UiButton
              icon="plus"
              @click="addTeamHandler"
            >
              {{ $t('teams.teams_buttons.add_team') }}
            </UiButton>
          </div>
          <template v-if="teamsList">
            <RouterLink
              v-for="item in teamsList"
              :key="item.id"
              :to="`/teams/${item.id}/details/users`"
              :class="[
                'teams-list__team',
                {'teams-list__team_default': item.id === 'all' },
                {'teams-list__team_active': activeTeam?.id === item.id }
              ]"
            >
              <UiTitle class="t:font-medium">
                {{ item.name }}
              </UiTitle>
              <div class="teams-list__team-count">
                {{ item.members_count }}
                <UiIcon
                  name="user"
                  color="var(--text-body-tertiary)"
                />
              </div>
              <div
                v-if="item.id !== 'all'"
                class="teams-list__team-actions"
              >
                <UiIcon
                  name="pen"
                  color="var(--text-primary)"
                  @click.stop.prevent="editTeamHandler(item)"
                />
                <UiIcon
                  v-if="item.type !== 'owners'"
                  name="trash-alt"
                  color="var(--text-negative)"
                  @click.stop.prevent="deleteTeamHandler(item)"
                />
              </div>
            </RouterLink>
          </template>
        </div>
      </div>
      <RouterView v-slot="{ Component }">
        <component
          :is="Component"
          :team="activeTeam"
          :permissions="dataPermissions"
          @refresh="refresh"
          @refresh-members="getTeams"
          @refresh-users-list="getUsers"
          @open-delete="deleteTeamHandler(activeTeam)"
          @open-edit="editTeamHandler(activeTeam)"
          @update-permissions="updatePermissions"
        />
      </RouterView>
    </div>
  </div>
</template>

<style lang="scss">
.teams-list {
  $root: &;

  margin: 16px 0;
  &__team {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    border: 1px solid var(--border-divider);
    cursor: pointer;
    transition: all .3s ease 0s;

    &:hover,
    &_active {
      background-color: var(--background-brand-subtle);

      &:not(#{$root}__team_default) {
        #{$root}__team-count {
          display: none;
        }
        #{$root}__team-actions {
          display: flex;
        }
      }
    }

    &_active {
      border-color: var(--border-focused);
    }
  }
  &__team-count {
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--site-text-color);
    font-size: 12px;
  }

  &__team-actions {
    display: none;
    align-items: center;
    gap: 12px;
  }
}
</style>
